import { useState, useCallback } from 'react';
import { Wallet, Landmark, AlertTriangle, Printer } from 'lucide-react';
import { reportApi } from '@/api/report.api';
import { useAuth } from '@/hooks/useAuth';
import { Spinner } from '@/components/ui/Spinner';
import { LocationSelect } from '@/components/reports/LocationSelect';
import { CostCentreSelect } from '@/components/reports/CostCentreSelect';
import { cn } from '@/utils/cn';
import type { CashBankBookDto, CashBankBookType } from '@/types/report.types';

/**
 * CashBookPage / BankBookPage — the two primary books of money movement.
 *
 * One component, two books. They are the same report over a different set of accounts, and
 * giving them a page each is how they drift apart.
 *
 * The account scope is NOT guessed at runtime. It comes from the account's subcategory
 * classification (migration 20260716_007), because nothing else in the chart of accounts
 * distinguishes cash from any other asset: account_category is empty on all 128 accounts and
 * allow_payment_posting is true on all of them. Scoping on the CASH payment-mode map would have
 * silently dropped Petty Cash; matching on name would have pulled in "Cash Over and Short",
 * which is an expense.
 *
 * Reads as Receipts / Payments rather than Debit / Credit. A cash account is an asset, so money
 * in debits it — but nobody looking for what they spent on Tuesday thinks in debits.
 */

const fmt = (n: number) =>
  n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtAmt = (n: number) => (n === 0 ? '—' : fmt(n));

const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch { return iso; }
};

const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

const inputCls = cn(
  'h-8 px-3 rounded-md border text-sm',
  'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
  'border-gray-300 dark:border-gray-600',
  'focus:outline-none focus:ring-2 focus:ring-brand-500',
);

interface Props { bookType: CashBankBookType }

export const CashBankBookPage = ({ bookType }: Props) => {
  const { user } = useAuth();
  const isBank = bookType === 'BANK';
  const title  = isBank ? 'Bank Book' : 'Cash Book';
  const Icon   = isBank ? Landmark : Wallet;

  const [fromDate, setFromDate]         = useState(firstOfMonth());
  const [toDate, setToDate]             = useState(today());
  const [locationId, setLocationId]     = useState('');
  const [costCenterId, setCostCenterId] = useState('');

  const [book, setBook]       = useState<CashBankBookDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const run = useCallback(async () => {
    setLoading(true); setError(null); setBook(null);
    try {
      const res = await reportApi.getCashBankBook({
        fromDate, toDate, bookType,
        accountId:    undefined,
        locationId:   locationId   || undefined,
        costCenterId: costCenterId || undefined,
      });
      setBook(res.data.data ?? null);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? `Failed to generate the ${title}.`);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, bookType, locationId, costCenterId, title]);

  const hasRows = !!book && book.accounts.some(a => a.rows.length > 0);

  return (
    <div className="flex flex-col h-full min-h-0">

      <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <Icon size={18} className="text-brand-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <h1 className="text-sm font-bold text-gray-900 dark:text-gray-100">{title}</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {isBank
              ? 'Every movement through the bank accounts, with running balance'
              : 'Every movement through the cash accounts, with running balance'}
          </p>
        </div>
        {hasRows && (
          <button
            onClick={() => window.print()}
            className={cn('h-8 px-3 flex items-center gap-1.5 text-xs rounded-md border',
              'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400',
              'hover:text-brand-600 hover:border-brand-400 transition-colors')}
          >
            <Printer size={13} /> Print
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="px-5 py-3 bg-gray-50 dark:bg-gray-800/40 border-b border-gray-200 dark:border-gray-700 print:hidden">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Date From">
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className={cn(inputCls, 'w-36')} />
          </Field>
          <Field label="Date To">
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className={cn(inputCls, 'w-36')} />
          </Field>
          <Field label="Location">
            <LocationSelect value={locationId} onChange={setLocationId} className={cn(inputCls, 'w-44')} />
          </Field>
          <Field label="Cost Centre">
            <CostCentreSelect value={costCenterId} onChange={setCostCenterId} className={cn(inputCls, 'w-44')} />
          </Field>
          <button
            onClick={run}
            disabled={loading}
            className={cn('h-8 px-4 rounded-md text-xs font-semibold bg-brand-600 hover:bg-brand-700 text-white',
              'disabled:opacity-50 disabled:cursor-not-allowed transition-colors')}
          >
            {loading ? <Spinner size="sm" className="text-white" /> : 'Generate'}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-5 py-2.5 text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-b border-red-200 dark:border-red-800">
          <AlertTriangle size={15} className="shrink-0" /> {error}
        </div>
      )}

      {loading && !book && <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>}

      {!loading && !book && !error && (
        <Empty icon={<Icon size={40} className="mx-auto mb-3 opacity-30" />}
               title="Choose a period and click Generate" />
      )}

      {/* No accounts classified — say that, rather than showing a zero balance that reads as
          "you have no cash". */}
      {book && book.accounts.length === 0 && (
        <Empty icon={<AlertTriangle size={40} className="mx-auto mb-3 opacity-30" />}
               title={`No accounts are classified as ${isBank ? 'Bank' : 'Cash'}`}
               subtitle="Set the account's subcategory on the Chart of Accounts to include it in this book." />
      )}

      {book && book.accounts.length > 0 && (
        <div className="flex-1 overflow-auto p-5 space-y-5 print:p-0">

          {/* Company totals */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">{title}</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">{book.companyName}</p>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {fmtDate(book.fromDate)} – {fmtDate(book.toDate)}
              </p>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <Stat label="Opening"  value={fmt(book.openingBalance)} />
              <Stat label="Receipts" value={fmt(book.totalReceipts)} tone="in" />
              <Stat label="Payments" value={fmt(book.totalPayments)} tone="out" />
              <Stat label="Closing"  value={fmt(book.closingBalance)}
                    tone={book.closingBalance < 0 ? 'out' : 'in'} />
            </div>
          </div>

          {/* One section per account */}
          {book.accounts.map(acct => (
            <div key={acct.accountId} className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-baseline gap-2">
                  <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{acct.accountCode}</span>
                  <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{acct.accountName}</span>
                </div>
                <div className="flex gap-5 text-xs">
                  <span className="text-gray-500 dark:text-gray-400">Opening <span className="font-mono text-gray-700 dark:text-gray-300">{fmt(acct.openingBalance)}</span></span>
                  <span className="text-gray-500 dark:text-gray-400">Closing <span className={cn('font-mono font-semibold',
                    acct.closingBalance < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-gray-800 dark:text-gray-200')}>
                    {fmt(acct.closingBalance)}</span></span>
                </div>
              </div>

              {acct.rows.length === 0 ? (
                <p className="px-4 py-6 text-center text-xs text-gray-400">No movement in this period.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50/60 dark:bg-gray-800/40 border-b border-gray-200 dark:border-gray-700">
                        {['Date', 'Type', 'Document #', 'Contra Account', 'Reference / Narration', 'Receipt', 'Payment', 'Balance'].map(h => (
                          <th key={h} className="px-3 py-2 text-left font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                      {/* Opening line — the balance has to start somewhere the reader can see. */}
                      <tr className="bg-gray-50/40 dark:bg-gray-800/20">
                        <td colSpan={7} className="px-3 py-2 text-right italic text-gray-500 dark:text-gray-400">Opening balance</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-gray-700 dark:text-gray-300">{fmt(acct.openingBalance)}</td>
                      </tr>
                      {acct.rows.map((r, i) => (
                        <tr key={`${r.journalId}-${i}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{fmtDate(r.postingDate)}</td>
                          <td className="px-3 py-2 whitespace-nowrap"><span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px] font-semibold text-gray-600 dark:text-gray-300">{r.transactionTypeCode}</span></td>
                          <td className="px-3 py-2 font-mono whitespace-nowrap text-gray-700 dark:text-gray-300">{r.documentNumber}</td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-400 max-w-40 truncate" title={r.contraAccountName ?? ''}>{r.contraAccountName ?? '—'}</td>
                          <td className="px-3 py-2 text-gray-500 dark:text-gray-400 max-w-48 truncate" title={r.reference || r.narration || ''}>{r.reference || r.narration || '—'}</td>
                          <td className="px-3 py-2 text-right font-mono whitespace-nowrap text-emerald-600 dark:text-emerald-400">{fmtAmt(r.receiptAmount)}</td>
                          <td className="px-3 py-2 text-right font-mono whitespace-nowrap text-rose-600 dark:text-rose-400">{fmtAmt(r.paymentAmount)}</td>
                          <td className={cn('px-3 py-2 text-right font-mono whitespace-nowrap font-semibold',
                            r.runningBalance < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-gray-800 dark:text-gray-200')}>
                            {fmt(r.runningBalance)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 dark:bg-gray-800 border-t-2 border-gray-300 dark:border-gray-600 font-semibold">
                        <td colSpan={5} className="px-3 py-2.5 text-right text-gray-600 dark:text-gray-400">Totals</td>
                        <td className="px-3 py-2.5 text-right font-mono text-emerald-600 dark:text-emerald-400">{fmt(acct.totalReceipts)}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-rose-600 dark:text-rose-400">{fmt(acct.totalPayments)}</td>
                        <td className={cn('px-3 py-2.5 text-right font-mono',
                          acct.closingBalance < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-gray-800 dark:text-gray-200')}>
                          {fmt(acct.closingBalance)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          ))}

          {user?.username && (
            <p className="text-[10px] text-gray-400 text-right print:block hidden">
              Generated by {user.username} · {new Date().toLocaleString('en-GB')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ── Bits ─────────────────────────────────────────────────────────────────────

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</label>
    {children}
  </div>
);

const Stat = ({ label, value, tone }: { label: string; value: string; tone?: 'in' | 'out' }) => (
  <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">{label}</p>
    <p className={cn('text-lg font-bold mt-0.5 font-mono',
      tone === 'in' ? 'text-emerald-600 dark:text-emerald-400'
        : tone === 'out' ? 'text-rose-600 dark:text-rose-400'
        : 'text-gray-700 dark:text-gray-300')}>
      {value}
    </p>
  </div>
);

const Empty = ({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle?: string }) => (
  <div className="flex-1 flex items-center justify-center">
    <div className="text-center text-gray-400 max-w-sm">
      {icon}
      <p className="text-sm font-medium">{title}</p>
      {subtitle && <p className="text-xs mt-1">{subtitle}</p>}
    </div>
  </div>
);

export const CashBookPage = () => <CashBankBookPage bookType="CASH" />;
export const BankBookPage = () => <CashBankBookPage bookType="BANK" />;
